/**
 * ✅ RESTAURANT NUMPAD İŞLEMLERİ HOOK'U
 * 
 * Bu hook, restoran modunda numpad (miktar girişi) işlemlerini yönetir:
 * - handleNumpadConfirm: Numpad onayı - miktar seçildi
 * - handleNumpadCancel: Numpad iptali
 * - handleReceiptPrintedConfirm: Adisyon yazdırıldı onayı
 * - handleReceiptPrintedConfirmCancel: Adisyon yazdırıldı iptali
 * 
 * QuickSaleScreen.tsx'den ayrıştırıldı - Ocak 2026
 */

import { useCallback } from 'react';
import type { CartItem } from '../../quickSale/types';

// Hook parametreleri için tip tanımı
export interface UseRestaurantNumpadOperationsParams {
  // Temel bilgiler
  isRestaurantMode: boolean;
  tableId: string | null;
  restaurantScreenId: number | null;
  companyId: string | null;
  waiterName: string | null;
  
  // Numpad state'leri
  numpadProduct: any;
  setNumpadProduct: React.Dispatch<React.SetStateAction<any>>;
  setNumpadModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  
  // Adisyon yazdırıldı state'leri
  isReceiptPrintedForCurrentTable: boolean;
  setIsReceiptPrintedForCurrentTable: React.Dispatch<React.SetStateAction<boolean>>;
  receiptPrintedConfirmOpen: boolean;
  setReceiptPrintedConfirmOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setReceiptPrintedNoPermissionOpen: React.Dispatch<React.SetStateAction<boolean>>;
  
  // Pending product state'leri (adisyon yazdırıldı onay için)
  pendingProductForReceiptConfirm: any;
  setPendingProductForReceiptConfirm: React.Dispatch<React.SetStateAction<any>>;
  pendingQuantityForReceiptConfirm: number;
  setPendingQuantityForReceiptConfirm: React.Dispatch<React.SetStateAction<number>>;
  pendingActionTypeForReceiptConfirm: 'addToCart' | 'numpadConfirm';
  setPendingActionTypeForReceiptConfirm: React.Dispatch<React.SetStateAction<'addToCart' | 'numpadConfirm'>>;
  
  // Adisyon açma için pending state'ler
  setPendingProduct: React.Dispatch<React.SetStateAction<any>>;
  setPendingQuantity: React.Dispatch<React.SetStateAction<number>>;
  setPendingMultiplier: React.Dispatch<React.SetStateAction<number>>;
  setConfirmOpenTableModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  
  // Sepet
  setCart: React.Dispatch<React.SetStateAction<CartItem[]>>;
  
  // Yetkiler
  waiterPermissions: {
    canCloseSale: boolean;
    canAddProductAfterReceiptPrint: boolean;
  };
  
  // Snackbar
  showSnackbar: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
  
  // Diğer fonksiyonlar
  addWaiterAction: (activeSaleId: string | number, actionType: string, details: any) => Promise<void>;

  /**
   * ✅ YENİ: Şube fiyatı garanti çözücü (race-condition fix).
   * Ürün eklenirken kesin fiyatı döndürür; useBranchPricing=true ürünün şube
   * fiyatı cache'de yoksa anında çeker. Şube fiyatı tanımlıysa KESİNLİKLE
   * kullanılır. Verilmezse mevcut numpadProduct.price'a fallback yapılır.
   */
  resolveBranchPrice?: (productId: string) => Promise<{ price: number; vatStatus: 'included' | 'excluded'; usedBranchPrice: boolean } | null>;
}

// Hook dönüş tipi
export interface UseRestaurantNumpadOperationsReturn {
  handleNumpadConfirm: (quantityToAdd: number, multiplier?: number) => Promise<void>;
  handleNumpadCancel: () => void;
  handleReceiptPrintedConfirm: () => void;
  handleReceiptPrintedConfirmCancel: () => void;
}

/**
 * Restaurant modu için numpad işlemleri hook'u
 */
export const useRestaurantNumpadOperations = ({
  isRestaurantMode,
  tableId,
  restaurantScreenId,
  companyId,
  waiterName,
  numpadProduct,
  setNumpadProduct,
  setNumpadModalOpen,
  isReceiptPrintedForCurrentTable,
  setIsReceiptPrintedForCurrentTable,
  setReceiptPrintedConfirmOpen,
  setReceiptPrintedNoPermissionOpen,
  pendingProductForReceiptConfirm,
  setPendingProductForReceiptConfirm,
  setPendingQuantityForReceiptConfirm,
  pendingActionTypeForReceiptConfirm,
  setPendingActionTypeForReceiptConfirm,
  setPendingProduct,
  setPendingQuantity,
  setPendingMultiplier,
  setConfirmOpenTableModalOpen,
  setCart,
  waiterPermissions,
  showSnackbar,
  addWaiterAction,
  resolveBranchPrice,
}: UseRestaurantNumpadOperationsParams): UseRestaurantNumpadOperationsReturn => {

  /**
   * Adisyon Yazdırıldı Onay Modalı - Kullanıcı onayladı
   */
  const handleReceiptPrintedConfirm = useCallback(() => {
    console.log('✅ [ADİSYON ONAY] Kullanıcı onayladı, ürün ekleniyor...');
    setReceiptPrintedConfirmOpen(false);
    
    if (pendingActionTypeForReceiptConfirm === 'addToCart' && pendingProductForReceiptConfirm) {
      // Numpad modalı aç
      setNumpadProduct(pendingProductForReceiptConfirm);
      setNumpadModalOpen(true);
    } else if (pendingActionTypeForReceiptConfirm === 'numpadConfirm' && pendingProductForReceiptConfirm) {
      // Numpad'i tekrar aç, seçilen miktar ile
      setNumpadProduct(pendingProductForReceiptConfirm);
      setNumpadModalOpen(true);
    }
    
    // Pending state'leri temizle
    setPendingProductForReceiptConfirm(null);
    setPendingQuantityForReceiptConfirm(1);
    
    // Adisyon durumunu geçici olarak sıfırla ki ürün eklenebilsin
    setIsReceiptPrintedForCurrentTable(false);
  }, [pendingActionTypeForReceiptConfirm, pendingProductForReceiptConfirm, setReceiptPrintedConfirmOpen, setNumpadProduct, setNumpadModalOpen, setPendingProductForReceiptConfirm, setPendingQuantityForReceiptConfirm, setIsReceiptPrintedForCurrentTable]);

  /**
   * Adisyon Yazdırıldı Onay Modalı - Kullanıcı iptal etti
   */
  const handleReceiptPrintedConfirmCancel = useCallback(() => {
    console.log('❌ [ADİSYON ONAY] Kullanıcı iptal etti');
    setReceiptPrintedConfirmOpen(false);
    setPendingProductForReceiptConfirm(null);
    setPendingQuantityForReceiptConfirm(1);
    setNumpadProduct(null);
  }, [setReceiptPrintedConfirmOpen, setPendingProductForReceiptConfirm, setPendingQuantityForReceiptConfirm, setNumpadProduct]);

  /**
   * Numpad Modal Onayı - Kullanıcı miktarı seçti
   */
  const handleNumpadConfirm = useCallback(async (quantityToAdd: number, multiplier: number = 1) => {
    if (!numpadProduct || !isRestaurantMode || !tableId || !restaurantScreenId || !companyId) {
      console.error('❌ [NUMPAD ONAY] Gerekli bilgiler eksik!');
      return;
    }

    // ADİSYON YAZDIRILDIYSA VE "Adisyon Sonrası Ürün Ekleme" YETKİSİ YOKSA ENGELLE
    if (isReceiptPrintedForCurrentTable && !waiterPermissions.canAddProductAfterReceiptPrint) {
      console.error('❌ [NUMPAD ONAY] Adisyon yazdırılmış, "Adisyon Sonrası Ürün Ekleme" yetkisi yok!');
      setNumpadModalOpen(false);
      setNumpadProduct(null);
      setReceiptPrintedNoPermissionOpen(true);
      return;
    }
    
    // ADİSYON YAZDIRILDIYSA VE YETKİSİ VARSA ONAY MODAL GÖSTER
    if (isReceiptPrintedForCurrentTable && waiterPermissions.canAddProductAfterReceiptPrint) {
      console.log('⚠️ [NUMPAD ONAY] Adisyon yazdırılmış, onay modalı gösteriliyor...');
      setPendingProductForReceiptConfirm(numpadProduct);
      setPendingQuantityForReceiptConfirm(quantityToAdd);
      setPendingActionTypeForReceiptConfirm('numpadConfirm');
      setNumpadModalOpen(false);
      setReceiptPrintedConfirmOpen(true);
      return;
    }

    try {
      // ✅ STOK KONTROLÜ: Stok eksiye düşemez ise miktarı kontrol et
      const itemCanBeNegative = numpadProduct.canBeNegative === true;
      const itemStockQuantity = numpadProduct.stockQuantity ?? 0;
      if (!itemCanBeNegative) {
        const totalRequested = quantityToAdd * multiplier;
        if (itemStockQuantity < totalRequested) {
          showSnackbar(
            `'${numpadProduct.name}' için stok yetersiz (Stok: ${itemStockQuantity}, İstenen: ${totalRequested}).`,
            'warning'
          );
          setNumpadModalOpen(false);
          setNumpadProduct(null);
          return;
        }
      }

      console.log('✅ [NUMPAD ONAY] Ürün ekleniyor...', {
        product: numpadProduct.name,
        quantity: quantityToAdd,
        multiplier: multiplier,
        canBeNegative: itemCanBeNegative,
        stockQuantity: itemStockQuantity
      });

      // ✅ ŞUBE FİYATI GARANTİSİ (race-condition fix):
      // Menü/opsiyonlu ürünlerde fiyat zaten opsiyon farklarıyla hesaplanmış
      // olduğundan dokunmuyoruz. Düz ürünlerde ise resolveBranchPrice ile
      // kesin fiyatı (gerekirse anında çekerek) alıyoruz; şube fiyatı tanımlıysa
      // KESİNLİKLE kullanılır, aksi halde ana fiyata düşülür.
      const hasMenuOrOptions = !!(numpadProduct.menuNormalItems || numpadProduct.selectedOptions || numpadProduct.totalPriceDifference);
      let productPrice = numpadProduct.price || numpadProduct.salePrice || 0;
      let productVatStatus = numpadProduct.vatStatus || 'excluded';
      if (!hasMenuOrOptions && resolveBranchPrice) {
        const resolved = await resolveBranchPrice(numpadProduct.id);
        if (resolved) {
          productPrice = resolved.price;
          productVatStatus = resolved.vatStatus;
          console.log('💰 [NUMPAD - ŞUBE FİYAT] Çözülen fiyat:', { product: numpadProduct.name, price: productPrice, usedBranchPrice: resolved.usedBranchPrice });
        }
      }
      const productUnit = numpadProduct.unit || 'adet';
      const productVatRate = numpadProduct.vatRate || 0;
      
      const { restaurantActiveSaleApi, restaurantActiveSaleItemApi } = await import('../../../../services/restaurantApiService');
      
      let activeSale = await restaurantActiveSaleApi.getByTableId(parseInt(tableId));
      
      if (!activeSale) {
        console.log('⚠️ [NUMPAD ONAY] Masa açık değil! Adisyon açma modalı açılıyor...');
        setPendingProduct(numpadProduct);
        setPendingQuantity(quantityToAdd);
        setPendingMultiplier(multiplier);
        setNumpadModalOpen(false);
        setConfirmOpenTableModalOpen(true);
        return;
      }

      const newCartItems: CartItem[] = [];

      for (let i = 0; i < multiplier; i++) {
        const createdItem = await restaurantActiveSaleItemApi.create({
          ActiveSaleId: activeSale.Id,
          ProductId: numpadProduct.id,
          ProductName: numpadProduct.name,
          Quantity: quantityToAdd,
          UnitPrice: productPrice,
          DiscountPercent: 0,
          AddedBy: waiterName || 'Bilinmeyen',
          Notes: null
        });

        console.log(`✅ [NUMPAD ONAY] SQL'e kaydedildi (${i + 1}/${multiplier})`, { sqlItemId: createdItem.Id });

        await addWaiterAction(activeSale.Id, 'add_item', {
          productId: numpadProduct.id,
          productName: numpadProduct.name,
          quantity: quantityToAdd,
          unitPrice: productPrice,
          sqlItemId: createdItem.Id,
          ...(multiplier > 1 ? { multiplier, multiplierIndex: i + 1 } : {})
        });

        const uniqueCartItemId = `${numpadProduct.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-m${i}`;

        newCartItems.push({
          cartItemId: uniqueCartItemId,
          id: numpadProduct.id,
          name: numpadProduct.name,
          price: productPrice,
          quantity: quantityToAdd,
          vatRate: productVatRate,
          vatStatus: productVatStatus as 'included' | 'excluded',
          unit: productUnit,
          sqlItemId: createdItem.Id,
          categoryId: numpadProduct.categoryId || '',
          hasMenu: numpadProduct.hasMenu || false,
          hasRecipe: numpadProduct.hasRecipe || false,
          selectedOptions: numpadProduct.selectedOptions || undefined,
          totalPriceDifference: numpadProduct.totalPriceDifference || undefined,
          menuNormalItems: numpadProduct.menuNormalItems || undefined,
        });
      }

      setCart(prevCart => [...prevCart, ...newCartItems]);
      
      console.log(`✅ [NUMPAD ONAY] İşlem tamamlandı! ${multiplier} adet ürün eklendi.`);
      
      setNumpadModalOpen(false);
      setNumpadProduct(null);
      
    } catch (error) {
      console.error('❌ [NUMPAD ONAY] Hata:', error);
      showSnackbar('Ürün eklenirken hata oluştu!', 'error');
    }
  }, [numpadProduct, isRestaurantMode, tableId, restaurantScreenId, companyId, waiterName, showSnackbar, addWaiterAction, isReceiptPrintedForCurrentTable, waiterPermissions.canAddProductAfterReceiptPrint, setNumpadModalOpen, setNumpadProduct, setReceiptPrintedNoPermissionOpen, setPendingProductForReceiptConfirm, setPendingQuantityForReceiptConfirm, setPendingActionTypeForReceiptConfirm, setReceiptPrintedConfirmOpen, setPendingProduct, setPendingQuantity, setPendingMultiplier, setConfirmOpenTableModalOpen, setCart, resolveBranchPrice]);

  /**
   * Numpad Modal İptal - Kullanıcı vazgeçti
   */
  const handleNumpadCancel = useCallback(() => {
    console.log('❌ [NUMPAD MODAL] Kullanıcı iptal etti');
    setNumpadModalOpen(false);
    setNumpadProduct(null);
  }, [setNumpadModalOpen, setNumpadProduct]);

  return {
    handleNumpadConfirm,
    handleNumpadCancel,
    handleReceiptPrintedConfirm,
    handleReceiptPrintedConfirmCancel,
  };
};

export default useRestaurantNumpadOperations;
